/**
 * @zendir/ui - DisplaySettingsContext
 *
 * Centralized context for app-wide display preferences.
 * Controls compact mode, accent colors, glass tint, and other display settings.
 *
 * Features:
 * - Compact mode toggle (affects all cards globally)
 * - Accent color selection (for card accents)
 * - Glass tint selection (for transparent themes)
 * - Color target control (both, accent-only, glass-only)
 * - LocalStorage persistence
 *
 * @example
 * ```tsx
 * import { DisplaySettingsProvider, useDisplaySettings } from '@zendir/ui';
 *
 * // Wrap your app
 * <DisplaySettingsProvider>
 *   <App />
 * </DisplaySettingsProvider>
 *
 * // Use in components
 * function MyCard() {
 *   const { compactMode, accentColor } = useDisplaySettings();
 *   return <Card compact={compactMode} />;
 * }
 * ```
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";

// =============================================================================
// Types
// =============================================================================

export type ColorTarget = "both" | "accent" | "glass";

export interface DisplaySettings {
  /** Global compact mode - affects all cards */
  compactMode: boolean;

  /** Accent color for cards and UI elements */
  accentColor: string;

  /** Glass tint color for transparent themes */
  glassTint: string;

  /** Which color setting to apply: both, accent-only, or glass-only */
  colorTarget: ColorTarget;

  /** Enable accent-colored borders on cards (for transparent themes) */
  accentBorders: boolean;
}

export interface DisplaySettingsContextValue extends DisplaySettings {
  /** Set compact mode on/off */
  setCompactMode: (value: boolean) => void;

  /** Toggle compact mode */
  toggleCompactMode: () => void;

  /** Set accent color */
  setAccentColor: (color: string) => void;

  /** Set glass tint color */
  setGlassTint: (color: string) => void;

  /** Set which color setting to apply */
  setColorTarget: (target: ColorTarget) => void;

  /** Set a color that applies based on current target */
  setColor: (color: string) => void;

  /** Set accent borders on/off */
  setAccentBorders: (value: boolean) => void;

  /** Toggle accent borders */
  toggleAccentBorders: () => void;

  /** Reset all settings to defaults */
  resetSettings: () => void;
}

// =============================================================================
// Preset Colors
// =============================================================================

export const PRESET_COLORS = {
  purple: "#8b5cf6",
  blue: "#3b82f6",
  cyan: "#06b6d4",
  teal: "#14b8a6",
  green: "#10b981",
  lime: "#84cc16",
  amber: "#f59e0b",
  orange: "#f97316",
  pink: "#ec4899",
  red: "#ef4444",
  slate: "#64748b",
} as const;

export const GLASS_TINTS = {
  ...PRESET_COLORS,
  clear: "rgba(255,255,255,0.05)",
  dark: "rgba(0,0,0,0.3)",
} as const;

export type PresetColorKey = keyof typeof PRESET_COLORS;
export type GlassTintKey = keyof typeof GLASS_TINTS;

// =============================================================================
// Default Values
// =============================================================================

const DEFAULT_SETTINGS: DisplaySettings = {
  compactMode: false,
  accentColor: PRESET_COLORS.purple,
  glassTint: PRESET_COLORS.purple,
  colorTarget: "both",
  accentBorders: true,
};

const STORAGE_KEY = "zendir-display-settings";

// =============================================================================
// Context
// =============================================================================

const DisplaySettingsContext =
  createContext<DisplaySettingsContextValue | null>(null);

// =============================================================================
// Provider Props
// =============================================================================

export interface DisplaySettingsProviderProps {
  children: ReactNode;

  /** Initial compact mode state */
  defaultCompactMode?: boolean;

  /** Initial accent color */
  defaultAccentColor?: string;

  /** Initial glass tint color */
  defaultGlassTint?: string;

  /** Initial color target */
  defaultColorTarget?: ColorTarget;

  /** Initial accent borders state */
  defaultAccentBorders?: boolean;

  /** Enable localStorage persistence (default: true) */
  persist?: boolean;
}

// =============================================================================
// Provider Component
// =============================================================================

export function DisplaySettingsProvider({
  children,
  defaultCompactMode,
  defaultAccentColor,
  defaultGlassTint,
  defaultColorTarget,
  defaultAccentBorders,
  persist = true,
}: DisplaySettingsProviderProps): React.ReactElement {
  // Initialize state from localStorage or defaults
  const [settings, setSettings] = useState<DisplaySettings>(() => {
    if (persist && typeof window !== "undefined") {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          return {
            compactMode:
              parsed.compactMode ??
              defaultCompactMode ??
              DEFAULT_SETTINGS.compactMode,
            accentColor:
              parsed.accentColor ??
              defaultAccentColor ??
              DEFAULT_SETTINGS.accentColor,
            glassTint:
              parsed.glassTint ??
              defaultGlassTint ??
              DEFAULT_SETTINGS.glassTint,
            colorTarget:
              parsed.colorTarget ??
              defaultColorTarget ??
              DEFAULT_SETTINGS.colorTarget,
            accentBorders:
              parsed.accentBorders ??
              defaultAccentBorders ??
              DEFAULT_SETTINGS.accentBorders,
          };
        }
      } catch {
        // Ignore parse errors
      }
    }

    return {
      compactMode: defaultCompactMode ?? DEFAULT_SETTINGS.compactMode,
      accentColor: defaultAccentColor ?? DEFAULT_SETTINGS.accentColor,
      glassTint: defaultGlassTint ?? DEFAULT_SETTINGS.glassTint,
      colorTarget: defaultColorTarget ?? DEFAULT_SETTINGS.colorTarget,
      accentBorders: defaultAccentBorders ?? DEFAULT_SETTINGS.accentBorders,
    };
  });

  // Persist to localStorage when settings change
  useEffect(() => {
    if (persist && typeof window !== "undefined") {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
      } catch {
        // Ignore storage errors
      }
    }
  }, [settings, persist]);

  // Setters
  const setCompactMode = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, compactMode: value }));
  }, []);

  const toggleCompactMode = useCallback(() => {
    setSettings((prev) => ({ ...prev, compactMode: !prev.compactMode }));
  }, []);

  const setAccentColor = useCallback((color: string) => {
    setSettings((prev) => ({ ...prev, accentColor: color }));
  }, []);

  const setGlassTint = useCallback((color: string) => {
    setSettings((prev) => ({ ...prev, glassTint: color }));
  }, []);

  const setColorTarget = useCallback((target: ColorTarget) => {
    setSettings((prev) => ({ ...prev, colorTarget: target }));
  }, []);

  // Set color based on current target
  const setColor = useCallback((color: string) => {
    setSettings((prev) => {
      switch (prev.colorTarget) {
        case "accent":
          return { ...prev, accentColor: color };
        case "glass":
          return { ...prev, glassTint: color };
        case "both":
        default:
          return { ...prev, accentColor: color, glassTint: color };
      }
    });
  }, []);

  const setAccentBorders = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, accentBorders: value }));
  }, []);

  const toggleAccentBorders = useCallback(() => {
    setSettings((prev) => ({ ...prev, accentBorders: !prev.accentBorders }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  // Memoized context value
  const value = useMemo<DisplaySettingsContextValue>(
    () => ({
      ...settings,
      setCompactMode,
      toggleCompactMode,
      setAccentColor,
      setGlassTint,
      setColorTarget,
      setColor,
      setAccentBorders,
      toggleAccentBorders,
      resetSettings,
    }),
    [
      settings,
      setCompactMode,
      toggleCompactMode,
      setAccentColor,
      setGlassTint,
      setColorTarget,
      setColor,
      setAccentBorders,
      toggleAccentBorders,
      resetSettings,
    ],
  );

  return (
    <DisplaySettingsContext.Provider value={value}>
      {children}
    </DisplaySettingsContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Access display settings context
 * Must be used within a DisplaySettingsProvider
 */
export function useDisplaySettings(): DisplaySettingsContextValue {
  const context = useContext(DisplaySettingsContext);

  if (!context) {
    throw new Error(
      "useDisplaySettings must be used within a DisplaySettingsProvider",
    );
  }

  return context;
}

/**
 * Optional hook that returns null if not within provider
 * Useful for components that should work with or without the provider
 */
export function useDisplaySettingsOptional(): DisplaySettingsContextValue | null {
  return useContext(DisplaySettingsContext);
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get effective compact mode value
 * Priority: explicit prop > context value > default (false)
 */
export function getEffectiveCompactMode(
  propValue: boolean | undefined,
  contextValue: boolean | undefined,
): boolean {
  if (propValue !== undefined) return propValue;
  if (contextValue !== undefined) return contextValue;
  return false;
}

/**
 * Get effective accent borders value
 * Priority: explicit prop > context value > default (true)
 */
export function getEffectiveAccentBorders(
  propValue: boolean | undefined,
  contextValue: boolean | undefined,
): boolean {
  if (propValue !== undefined) return propValue;
  if (contextValue !== undefined) return contextValue;
  return true;
}

/**
 * Hook to get card border style based on accentBorders setting
 * Returns the appropriate border style for transparent themes
 */
export function useCardBorderStyle(
  tokens: any,
  isTransparentTheme: boolean,
): React.CSSProperties {
  const displaySettings = useDisplaySettingsOptional();
  const accentBorders = displaySettings?.accentBorders ?? true;

  if (!isTransparentTheme) {
    return (
      tokens.colors.border.cardStyle ?? {
        border: `1px solid ${tokens.colors.border.muted}`,
      }
    );
  }

  if (accentBorders) {
    return tokens.colors.border.cardStyle ?? {};
  }

  // Fallback to subtle neutral border when accent borders disabled
  return { border: "1px solid rgba(255, 255, 255, 0.08)" };
}

export default DisplaySettingsProvider;
